import React, { useState, useRef, useEffect } from "react";
const CustomSkillsWidget = (props) => {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    onChange,
    onBlur,
    onFocus,
    placeholder,
  } = props;

  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");

  // Parse the current value (JSON stringified array) into an array of tags
  const parseTags = (val) => {
    if (val === null || val === undefined || val === "" || val === '""' || val === "undefined") {
      return [];
    }

    // If already an array, handle directly
    if (Array.isArray(val)) {
      return val
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return item.tag || "";
          }
          return String(item);
        })
        .filter((s) => s.length > 0);
    }

    // If not a string, convert to string first
    if (typeof val !== "string") {
      return [];
    }

    try {
      let parsed = JSON.parse(val);
      // Handle double-stringified case: "\"[]\""
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          // It's a plain string, split by comma
          return parsed
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        }
      }
      if (Array.isArray(parsed)) {
        // Handle array of objects with "tag" property (legacy format)
        return parsed
          .map((item) => {
            if (typeof item === "object" && item !== null) {
              return item.tag || "";
            }
            return String(item);
          })
          .filter((s) => s.length > 0);
      }
      return [];
    } catch (e) {
      // Not valid JSON — treat as comma-separated string
      return String(val)
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
  };

  const tags = parseTags(value);

  const addSkills = (text) => {
    const newSkills = text
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (newSkills.length === 0) return;

    const updatedTags = [...tags];
    newSkills.forEach((skill) => {
      // Avoid duplicates (case-insensitive)
      if (!updatedTags.some((t) => t.toLowerCase() === skill.toLowerCase())) {
        updatedTags.push(skill);
      }
    });

    onChange(JSON.stringify(updatedTags));
    setInputValue("");
  };

  const removeTag = (index) => {
    const updatedTags = tags.filter((_, i) => i !== index);
    onChange(updatedTags.length > 0 ? JSON.stringify(updatedTags) : "");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (inputValue.trim()) {
        addSkills(inputValue);
      }
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleBlur = (e) => {
    // On blur, also save whatever is in the input as a tag
    if (inputValue.trim()) {
      addSkills(inputValue);
    }
    if (onBlur) {
      onBlur(id, value);
    }
  };

  const handleFocus = (e) => {
    if (onFocus) {
      onFocus(id, value);
    }
  };

  return (
    <div className="skills-widget-container">
      <input
        ref={inputRef}
        id={id}
        className="form-control"
        type="text"
        value={inputValue}
        placeholder={placeholder || "Type a skill and press Enter"}
        required={required && tags.length === 0}
        disabled={disabled}
        readOnly={readonly}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      {tags.length > 0 && (
        <div className="skills-tags-container">
          {tags.map((tag, index) => (
            <span key={`${tag}-${index}`} className="skills-tag">
              {tag}
              {!disabled && !readonly && (
                <button
                  type="button"
                  className="skills-tag-remove"
                  onClick={() => removeTag(index)}
                  aria-label={`Remove ${tag}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <style>{`
        .skills-widget-container {
          width: 100%;
        }
        .skills-tags-container {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 8px;
        }
        .skills-tag {
          display: inline-flex;
          align-items: center;
          background-color: #e8f4fd;
          border: 1px solid #b3d9f2;
          border-radius: 4px;
          padding: 4px 8px;
          font-size: 13px;
          color: #2c6fa0;
          line-height: 1.4;
        }
        .skills-tag-remove {
          background: none;
          border: none;
          color: #2c6fa0;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          margin-left: 6px;
          padding: 0 2px;
          font-weight: bold;
          opacity: 0.7;
        }
        .skills-tag-remove:hover {
          opacity: 1;
          color: #c0392b;
        }
      `}</style>
    </div>
  );
};

export default CustomSkillsWidget;
